import React from 'react';
import { Link } from 'react-router-dom';
import { money, day, toNumber } from './format';
import { ExportCsvButton, InfoTip } from './PageTools';
import { dealReportPath } from './links';

// Brian, 27 October 2026: "i guess what i really want to see instead of MTD
// pnl is MTD cash generated from deals...we cant eat unrealized". Cash in from
// each deal this month, what went back in, and every movement behind it, read
// from /reports/cash-generated/ (reports/services/cash_generated.py).
//
// And cash is not profit: "yes we generated almost $50k in Live Oak but we
// lost money on a net basis" (D-290). The units cost $50,000.00 in September
// and sold for $49,673.58 in October, so every cash figure carries what it made
// over what it cost on the book beside it.

const KIND_ORDER_IN = ['trading', 'redemptions', 'interest', 'dividends', 'fees', 'costs_reimbursed', 'other'];
const KIND_ORDER_OUT = ['invested', 'draws', 'shorts_covered', 'costs'];

const signed = (line) => toNumber(line.usd);
const tone = (v) => (v < 0 ? 'mtd-negative' : v > 0 ? 'mtd-positive' : '');
// "Loss ($326.42)", never a bare figure a reader has to read the sign of.
export const made = (v) => `${v < 0 ? 'Loss' : 'Profit'} ${money(v)}`;

export function cashGeneratedSummary(data) {
  const total = data?.total;
  if (!total) return null;
  return {
    generated: toNumber(total.generated_usd),
    deployed: toNumber(total.deployed_usd),
    net: toNumber(total.net_usd),
    held: toNumber(total.held_against_shorts_usd) || 0,
    capitalBack: toNumber(total.capital_back_usd),
    profit: toNumber(total.profit_usd),
    missing: (data.deals_not_in_total || []).length,
  };
}

const dealName = (row) => `${row.company}${row.instrument ? ` · ${row.instrument}` : ''}`;

// What a movement made, where that is not just its cash: a sale or repayment
// against what it cost, a short when it closes.
function madeOn(line) {
  const profit = toNumber(line.profit_usd);
  if (profit === null) return null;
  if (line.capital_back_usd !== undefined) return `Cost on the book ${money(line.capital_back_usd)} · ${made(profit)}`;
  if (line.kind === 'short' && line.direction !== 'in') return `${made(profit)} on the short`;
  return null;
}

function Movement({ line }) {
  const native = line.currency && line.currency !== 'USD';
  const usd = signed(line);
  const onIt = madeOn(line);
  return <div className="mtd-account">
    <span>{day(line.date)} · {line.kind_label}
      <small>{line.description}{line.instrument ? ` · ${line.instrument}` : ''}</small>
      {native && <small>{money(line.amount, { currency: line.currency })}</small>}
      {toNumber(line.held_against_short) > 0 && <small>{money(line.held_against_short, { currency: line.currency })} of it held: the shares are still owed.</small>}
      {onIt && <small>{onIt}</small>}
    </span>
    <strong className={usd < 0 ? 'mtd-negative' : 'mtd-positive'}>{usd === null ? money(line.amount, { currency: line.currency }) : money(usd)}</strong>
  </div>;
}

export default function MTDCashGenerated({ data, loading, error }) {
  const summary = cashGeneratedSummary(data);
  const labels = data?.kind_labels || {};
  const deals = data?.deals || [];
  const movements = deals.flatMap((row) => row.events.map((line) => ({ ...line, deal: dealName(row), book: row.book })));
  return <div className="mtd-breakdown">
    <h2>Cash generated from deals{data ? ` · ${day(data.start)} to ${day(data.as_of)}` : ''}</h2>
    <p>
      Cash that came in from deals this month: shares sold, notes repaid, interest, dividends and fees.
      A mark is not cash, so nothing unrealized is in it.
      <InfoTip wide>
        Lenders' money coming in and payouts to them are financing, not cash generated, and are left out.
        A short sale's cash is held while the shares are still owed and counts, net of buying them back, once the short closes.
        A receipt that is a claim on another Helena entity, or a deal's own distribution arriving at a Helena account, is listed under the deal and not counted.
        Krona is converted at each day's fixing. The same rules decide what counts as proceeds on Cash Owed.
        Profit or loss is the cash less what it bought or gave up on the book: a sale less what the shares cost, a note repaid less its carrying value.
        A purchase makes nothing, a cost is a loss, and interest, dividends and fees are all profit.
        A short makes its profit or loss when it closes, including one closed by delivering shares, which moves no cash.
      </InfoTip>
    </p>
    {loading && !data ? <p>Loading cash generated…</p> : !summary ? <p>{error ? `Cash generated could not be loaded: ${error}. Refresh to retry.` : 'Cash generated could not be loaded. Refresh to retry.'}</p> : <>
      {KIND_ORDER_IN.filter((k) => data.total.generated_by_kind[k]).map((k) => <div className="mtd-account" key={k}><span>{labels[k] || k}</span><strong>{money(data.total.generated_by_kind[k])}</strong></div>)}
      <div className="mtd-total"><span>Cash generated · MTD</span><strong className="mtd-positive">{money(summary.generated)}</strong></div>
      {KIND_ORDER_OUT.filter((k) => data.total.deployed_by_kind[k]).map((k) => <div className="mtd-account" key={k}><span>{labels[k] || k}</span><strong className="mtd-negative">{money(-toNumber(data.total.deployed_by_kind[k]))}</strong></div>)}
      <div className="mtd-total"><span>Net cash from deals · MTD<small>Generated less what went back into deals</small></span><strong className={summary.net < 0 ? 'mtd-negative' : 'mtd-positive'}>{money(summary.net)}</strong></div>
      {summary.profit !== null && <div className="mtd-total"><span>Profit or loss · MTD<small>What the month's cash made over what it cost on the book. {money(summary.capitalBack)} of the cash generated was the cost of what was sold or repaid coming back.</small></span><strong className={tone(summary.profit)}>{money(summary.profit)}</strong></div>}
      {summary.held > 0 && <p role="note">{money(summary.held)} more came in from short sales whose shares are still owed. It is held, not generated, until the shorts close.</p>}
      {summary.missing > 0 && <p role="status">Not in the totals, because a krona fixing they need is not loaded: {data.deals_not_in_total.map(dealName).join(', ')}.</p>}
      {(data.deals_not_in_profit || []).length > 0 && <p role="status">Not in the profit, because a fixing it needs is not loaded: {data.deals_not_in_profit.map(dealName).join(', ')}.</p>}

      <h3 className="mtd-subhead">By deal</h3>
      {!deals.length && <p>No deal has moved cash this month.</p>}
      {deals.map((row) => <details key={row.deal_id} className="mtd-category">
        <summary>
          <span>{dealName(row)}<small>{row.book}{toNumber(row.capital_back_usd) ? ` · cost on the book ${money(row.capital_back_usd)}` : ''}{row.generated_usd !== null && toNumber(row.deployed_usd) ? ` · ${money(row.deployed_usd)} went back in · net ${money(row.net_usd)}` : ''}</small></span>
          <span className="mtd-figures">
            <strong>{row.generated_usd === null ? Object.entries(row.generated_native).map(([c, a]) => money(a, { currency: c })).join(' + ') || '—' : money(row.generated_usd)}</strong>
            {row.profit_usd !== null && row.profit_usd !== undefined && <small className={tone(toNumber(row.profit_usd))}>{made(toNumber(row.profit_usd))}</small>}
          </span>
        </summary>
        {row.unavailable_because && <div className="mtd-account"><span>{row.unavailable_because}</span></div>}
        {row.events.map((line, i) => <Movement key={i} line={line} />)}
        {row.excluded.map((line, i) => <div className="mtd-account" key={`x${i}`}><span>{day(line.date)} · Not counted<small>{line.description}</small><small>{line.reads_as}</small></span><strong>{money(line.usd ?? line.amount, { currency: line.usd === null || line.usd === undefined ? line.currency : 'USD' })}</strong></div>)}
        <div className="mtd-account"><span><Link to={dealReportPath(row.deal_id)}>Open the deal report</Link></span></div>
      </details>)}
      <div className="mtd-tools">
        <ExportCsvButton
          filename="cash-generated-mtd"
          rows={movements}
          columns={[
            { label: 'Deal', value: (r) => r.deal },
            { label: 'Book', value: (r) => r.book },
            { label: 'Date', value: (r) => r.date },
            { label: 'In or out', value: (r) => r.direction },
            { label: 'What', value: (r) => r.kind_label },
            { label: 'Description', value: (r) => r.description },
            { label: 'Amount', value: (r) => r.amount },
            { label: 'Currency', value: (r) => r.currency },
            { label: 'USD', value: (r) => r.usd },
            { label: 'Cost on the book (USD)', value: (r) => r.capital_back_usd ?? '' },
            { label: 'Profit or loss (USD)', value: (r) => r.profit_usd ?? '' },
          ]}
        />
      </div>
    </>}
  </div>;
}
